import React, { useState } from "react";
import { Booking } from "../types/booking";
import { Check, Clock, Calendar, Play, UserX, XCircle } from "lucide-react";

interface BookingCardProps {
  booking: Booking;
  style?: React.CSSProperties;
  onClick?: () => void;
  bgColor?: string;
  isLastColumn?: boolean;
  showAbove?: boolean;
}

const BookingCard: React.FC<BookingCardProps> = ({
  booking,
  style,
  onClick,
  bgColor: propBgColor,
  isLastColumn = false,
  showAbove = false,
}) => {
  const [hovered, setHovered] = useState(false);

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date
      .toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      })
      .toLowerCase();
  };

  const timeRange = `${formatTime(booking.start)} - ${formatTime(booking.end)}`;

  const staffBaseColor = booking.categoryColor || propBgColor || "#885ABB";

  const statusConfig = {
    completed: {
      text: "Completed",
      icon: <Check size={12} strokeWidth={4} />,
    },
    pending: {
      text: "Pending",
      icon: <Clock size={12} strokeWidth={4} />,
    },
    booked: {
      text: "Booked",
      icon: <Calendar size={12} strokeWidth={4} />,
    },
    "in progress": {
      text: "In Progress",
      icon: <Play size={12} strokeWidth={4} />,
    },
    "no show": {
      text: "No Show",
      icon: <UserX size={12} strokeWidth={4} />,
    },
    cancelled: {
      text: "Cancelled",
      icon: <XCircle size={12} strokeWidth={4} />,
    },
  };

  const currentStatus = statusConfig[booking.status as keyof typeof statusConfig] || statusConfig.booked;

  const heightStr = style?.height?.toString() || "";
  const heightPx = parseInt(heightStr.replace("px", "")) || 100;

  const isUltraShort = heightPx < 45;
  const isShort = heightPx < 85;

  const topStr = style?.top?.toString() || "";
  const topPx = parseInt(topStr.replace("px", "")) || 0;

  return (
    <div
      className={`absolute shadow-none lg:shadow-sm cursor-pointer overflow-visible transition-all duration-200 border-0 md:border md:border-white/20
        ${isUltraShort
          ? "p-1 rounded-md"
          : isShort
            ? "p-1.5 rounded-md"
            : "p-3 rounded-lg"
        } 
        ${hovered ? "z-[1900] ring-2 ring-white/50" : "z-10"}`}
      style={{
        backgroundColor: staffBaseColor,
        ...style,
      }}
      onClick={() => {
        setHovered(false);
        onClick?.();
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div
        className={`flex flex-col h-full ${isUltraShort ? "gap-0.5" : isShort ? "gap-1" : "gap-2"
          }`}
      >
        <div className="flex flex-col min-w-0">
          <div
            className={`font-black text-white truncate uppercase tracking-tight leading-tight 
            ${isUltraShort
                ? "text-[9px]"
                : isShort
                  ? "text-[11px]"
                  : "text-[13px]"
              }`}
          >
            {booking.client}
          </div>
          {!isShort && (
            <div className="text-white/80 text-[10px] font-bold flex items-center gap-1 mt-0.5">
              {formatTime(booking.start)} - {formatTime(booking.end)}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between w-full mt-auto">
          {isUltraShort ? (
            <div className="text-white scale-75 origin-left">
              {currentStatus.icon}
            </div>
          ) : (
            <div
              className={`flex items-center rounded-full bg-white/10 text-white shadow-sm ring-1 ring-white/20
                ${isShort ? "gap-1 px-1.5 py-0" : "gap-1.5 px-2 py-0.5"}`}
            >
              <div className={isShort ? "scale-75 origin-left" : ""}>
                {currentStatus.icon}
              </div>
              <span
                className={`font-black uppercase tracking-wider ${isShort ? "text-[8px]" : "text-[9px]"
                  }`}
              >
                {currentStatus.text}
              </span>
            </div>
          )}

          <span
            className={`text-white font-bold tracking-tight shrink-0
            ${isUltraShort
                ? "text-[8px]"
                : isShort
                  ? "text-[9px]"
                  : "text-[10px]"
              }`}
          >
            {formatTime(booking.start)}
          </span>
        </div>
      </div>

      {/* Hover popup kept for details without affecting card scale */}
      {hovered && (
        <div
          className={`absolute z-[2000] w-64 p-4 text-xs rounded-xl shadow-2xl backdrop-blur-md border border-white/20
            ${showAbove
              ? (topPx < 180 ? "top-full left-0 mt-2" : "bottom-full left-0 mb-2")
              : isLastColumn
                ? "right-full mr-2 top-0"
                : "left-full ml-2 top-0"
            }`}
          style={{ backgroundColor: staffBaseColor, color: "white" }}
        >
          <div className="flex items-center gap-2 mb-2 pb-2 border-b border-white/20">
            <div className="p-1.5 bg-white/20 rounded-lg">
              {currentStatus.icon}
            </div>
            <div className="font-bold uppercase tracking-widest text-[10px]">
              {booking.status} Booking
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="opacity-70 text-[10px] uppercase">Client:</span>
              <span className="font-bold">{booking.client}</span>
            </div>
            <div className="flex justify-between">
              <span className="opacity-70 text-[10px] uppercase">Time:</span>
              <span className="font-bold">{timeRange}</span>
            </div>
            <div className="flex justify-between">
              <span className="opacity-70 text-[10px] uppercase">Staff:</span>
              <span className="font-bold">{booking.staff}</span>
            </div>
            {/* <div className="flex justify-between pt-1 border-t border-white/10 mt-1">
              <span className="opacity-70 text-[10px] uppercase">Payment:</span>
              <span className="font-bold">
                {booking.amountPaid} / {booking.amountToPay} {booking.currency}
              </span>
            </div> */}
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingCard;
